import { Injectable, signal } from "@angular/core";

/**
 * Tracks WHICH price ids the author has explicitly unlocked for editing the
 * two money amounts (`dutyFreeAmount` / `taxIncludedAmount`).
 *
 * Holding `enrichment:publish` on the Administrator role is what makes an
 * author *able* to change a price (see `EnrichmentPricing.mayEditPrice`);
 * this store is the second, deliberate step that says they *mean* to
 * on this particular price right now. It exists because those two amounts are
 * the only money the portal authors, and because they ride along on every
 * save whether or not anyone touched them — see `stripUneditableAmounts`.
 *
 * It is NOT a view concern, which is why it does not live in
 * `EnrichmentPricing`: that component sits inside the edit shell's
 * `@switch`/`@case ('price-config')` and is DESTROYED on every tab switch,
 * while the edited amounts live on in the store. A component-local `unlocked`
 * signal would therefore produce silent data loss — unlock a price, type an
 * amount, visit the Shelf tab, come back, and the component re-mounts locked
 * while the model still holds the edit, so the field shows the new value
 * greyed out and the following save strips it. The author watches a figure
 * they entered disappear from a save that reported success.
 *
 * Provided in `EnrichmentEditPackage`'s `providers` (NOT root) so the set
 * dies with the route — a fresh edit session always starts locked, like
 * {@link EnrichmentPriceDirtyStore} beside it.
 *
 * Exactly two things re-lock a price: the author locking it (which also
 * reverts what they typed — see `EnrichmentPricing.cancelPriceAmountEdit`)
 * and a save of that price succeeding. A tab switch does neither, so
 * "Locked" always means "showing what is stored".
 */
@Injectable()
export class EnrichmentPriceLockStore {
  private readonly ids = signal<ReadonlySet<string>>(new Set());

  /** Read-only signal of the price ids currently unlocked for amount edits.
   *  Read it (not {@link isUnlocked}) from a `computed` so the read is
   *  tracked. */
  readonly unlockedIds = this.ids.asReadonly();

  /** Untracked point read — for imperative callers such as the save
   *  coordinator. A template or `computed` should go through
   *  {@link unlockedIds} instead. */
  isUnlocked(id: string | undefined): boolean {
    return !!id && this.ids().has(id);
  }

  unlock(id: string): void {
    if (!id || this.ids().has(id)) return;
    const next = new Set(this.ids());
    next.add(id);
    this.ids.set(next);
  }

  lock(id: string): void {
    if (!id || !this.ids().has(id)) return;
    const next = new Set(this.ids());
    next.delete(id);
    this.ids.set(next);
  }

  clear(): void {
    if (this.ids().size === 0) return;
    this.ids.set(new Set());
  }
}
